/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect, useMemo, useCallback, useRef } from "react"

// react-router components
import { Routes, Route, Navigate, useLocation } from "react-router-dom"

// @mui material components
import { ThemeProvider } from "@mui/material/styles"
import CssBaseline from "@mui/material/CssBaseline"

// Material Dashboard 2 React example components
import Sidenav from "monitor/Sidenav"
import Configurator from "monitor/Configurator"

// Material Dashboard 2 React themes
import theme from "assets/theme"

// Material Dashboard 2 React Dark Mode themes
import themeDark from "assets/theme-dark"

import useLocalStorage from "./useLocalStorage"
// Material Dashboard 2 React routes
import MyRoutes, { buildRoutesFromServer } from "routes"

import config from "config"
import { useMaterialUIController, setMiniSidenav } from "context"
import { fetchImageBase64, IMAGE_TYPES } from "monitor/Configurator/service"
import { invalidateAllImageCaches } from "utils/remoteImageCache"
import { withBasename } from "appBasePath"

// Images
import brandWhite from "assets/images/logo-ct.png"
import packageInfo from "../package.json"
import { updateSwalDarkMode, showToast } from "utils/swalToast"

// Save the original fetch
const originalFetch = window.fetch
// Guard against multiple concurrent 401 responses triggering duplicate redirects
let embedRedirecting = false
const BRAND_LOGO_CACHE_KEY = "app.brandLogo.dataUrl"
const BRAND_LOGO_CACHE_TS_KEY = "app.brandLogo.ts"
const BRAND_LOGO_CACHE_TTL_MS = 1000 * 60 * 30 // 30 min
const BRAND_LOGO_EMPTY_VALUE = "__EMPTY__"
const EMBED_SESSION_FLAG_KEY = "embed.session.active"
const EMBED_API_KEY_STORAGE_KEY = "embed.session.apiKey"
const EMBED_PARENT_ORIGIN = config.embedParentOrigin

const notifyParent = (type, extra = {}) => {
  if (window.parent && window.parent !== window) {
    window.parent.postMessage(
      {
        source: "middleware-portal-monitoreo",
        type,
        ...extra,
      },
      EMBED_PARENT_ORIGIN
    )
  }
}

const clearAuthStorage = () => {
  sessionStorage.removeItem("token")
  sessionStorage.removeItem("role")
  sessionStorage.removeItem("username")
  sessionStorage.removeItem("company")
  sessionStorage.removeItem("companyId")
  localStorage.removeItem("token")
  localStorage.removeItem("role")
  localStorage.removeItem("username")
  localStorage.removeItem("company")
  localStorage.removeItem("companyId")
  invalidateAllImageCaches()
}

const getBase64FromPayload = (payload) => {
  if (!payload) return null
  if (typeof payload === "string") return payload

  if (Array.isArray(payload)) {
    for (const item of payload) {
      const nested = getBase64FromPayload(item)
      if (nested) return nested
    }
    return null
  }

  if (typeof payload !== "object") return null

  const candidateKeys = ["base64", "imageBase64", "data", "content", "value", "file"]
  for (const key of candidateKeys) {
    if (typeof payload[key] === "string" && payload[key].trim()) {
      return payload[key]
    }
  }

  for (const value of Object.values(payload)) {
    const nested = getBase64FromPayload(value)
    if (nested) return nested
  }

  return null
}

const toImageDataUrl = (payload) => {
  const rawValue = getBase64FromPayload(payload)
  if (!rawValue) return null

  const trimmed = rawValue.trim()
  if (trimmed.startsWith("data:image/")) {
    return trimmed
  }

  const normalized = trimmed.replace(/\s/g, "")
  const mimeType = normalized.startsWith("/9j/")
    ? "image/jpeg"
    : normalized.startsWith("R0lGOD")
      ? "image/gif"
      : normalized.startsWith("PHN2Zy")
        ? "image/svg+xml"
        : "image/png"

  return `data:${mimeType};base64,${normalized}`
}

const getCachedBrandLogo = () => {
  const rawTs = localStorage.getItem(BRAND_LOGO_CACHE_TS_KEY)
  const cachedValue = localStorage.getItem(BRAND_LOGO_CACHE_KEY)
  const ts = Number(rawTs || 0)
  const isFresh = Date.now() - ts < BRAND_LOGO_CACHE_TTL_MS

  if (!isFresh || cachedValue === null) {
    return undefined
  }

  if (cachedValue === BRAND_LOGO_EMPTY_VALUE) {
    return null
  }

  return cachedValue
}

// Override fetch
window.fetch = async (...args) => {
  try {
    // Call the original fetch
    const response = await originalFetch(...args)

    // Check the response status for specific handling
    if (response.status === 401 || response.status === 403) {
      const hasActiveSession = Boolean(
        sessionStorage.getItem("token") || localStorage.getItem("token")
      )
      const isAuthenticationPath =
        window.location.pathname.startsWith(withBasename("/authentication")) ||
        window.location.pathname.startsWith(withBasename("/embed"))

      // Evita bucles en login/reset cuando un endpoint público responde 403.
      // embedRedirecting evita que múltiples fetch concurrentes con 401 disparen el redirect varias veces.
      if (hasActiveSession && !isAuthenticationPath && !embedRedirecting) {
        const isEmbedSession = sessionStorage.getItem(EMBED_SESSION_FLAG_KEY) === "1"
        const embedApiKey = sessionStorage.getItem(EMBED_API_KEY_STORAGE_KEY) || ""

        if (isEmbedSession && embedApiKey) {
          // Para sesiones embed, solo redirigir si el token está realmente expirado.
          // Un 401/403 con token válido es un error de permisos, no de autenticación,
          // y redirigir causaría un loop infinito /embed → /dashboard → /embed.
          const storedToken = sessionStorage.getItem("token")
          let isTokenExpired = true
          if (storedToken) {
            try {
              const payload = JSON.parse(atob(storedToken.split(".")[1]))
              const exp = payload?.exp
              // Token está vigente si exp existe y no expira en los próximos 30s
              isTokenExpired = !exp || exp * 1000 - Date.now() < 30_000
            } catch {
              // token malformado → tratar como expirado
            }
          }

          if (!isTokenExpired) {
            // Token válido — el 401 es un error de permisos, no de sesión.
            // No redirigir; dejar que el efecto de rutas maneje la respuesta.
            return response
          }

          embedRedirecting = true
          clearAuthStorage()
          notifyParent("PORTAL_EMBED_AUTH_EXPIRED")
          window.location.href = withBasename(`/embed?apikey=${encodeURIComponent(embedApiKey)}`)
          return response
        }

        embedRedirecting = true
        clearAuthStorage()
        sessionStorage.removeItem(EMBED_SESSION_FLAG_KEY)
        sessionStorage.removeItem(EMBED_API_KEY_STORAGE_KEY)
        showToast({
          title: "No tiene acceso al recurso especificado!",
          icon: "error",
        })
        window.location.href = withBasename("/authentication/sign-in")
      }
    }
    if (response.status === 500) {
      // Redirect to a security page for 403 errors
      showToast({
        title: "Ocurrio un error al procesar la solicitud. Contacte con el soporte técnico.",
        icon: "error",
      })
    }

    return response // Return the response for further processing
  } catch (error) {
    // Handle network errors
    if (error instanceof TypeError && error.message === "Failed to fetch") {
      // Server is offline or unreachable
      console.error("Server is offline:", error)
      showToast({
        title:
          "No tienes acceso al servidor, verifica tu conexion o contacta con el soporte tecnico",
        icon: "error",
      })
    } else {
      // Re-throw other errors to handle them upstream
      console.error("Unexpected fetch error:", error)
      throw error
    }
  }
}

export default function App() {
  const [role] = useLocalStorage("role", "user")
  const [controller, dispatch] = useMaterialUIController()
  const {
    miniSidenav,
    layout,
    sidenavColor,
    darkMode,
  } = controller

  useEffect(() => {
    updateSwalDarkMode(darkMode)
  }, [darkMode])

  const [onMouseEnter, setOnMouseEnter] = useState(false)
  const { pathname } = useLocation()
  const [routes, setRoutes] = useState([])
  const lastRoutesJSON = useRef("")
  const [routesReady, setRoutesReady] = useState(false)
  const [authReady, setAuthReady] = useState(false)
  const [isAuth, setIsAuth] = useState(false)
  const [brandLogo, setBrandLogo] = useState(() => {
    const cached = getCachedBrandLogo()
    return cached || brandWhite
  })

  const fetchAndCacheBrandLogo = useCallback(async () => {
    if (!isAuth) {
      setBrandLogo(brandWhite)
      return
    }

    try {
      const companyId = (sessionStorage.getItem("companyId") || localStorage.getItem("companyId") || "").trim()
      if (!companyId) {
        setBrandLogo(brandWhite)
        return
      }
      const payload = await fetchImageBase64(IMAGE_TYPES.BRAND, companyId)
      const nextLogo = toImageDataUrl(payload)

      localStorage.setItem(BRAND_LOGO_CACHE_TS_KEY, String(Date.now()))
      if (nextLogo) {
        localStorage.setItem(BRAND_LOGO_CACHE_KEY, nextLogo)
        setBrandLogo(nextLogo)
        return
      }

      localStorage.setItem(BRAND_LOGO_CACHE_KEY, BRAND_LOGO_EMPTY_VALUE)
      setBrandLogo(brandWhite)
    } catch {
      setBrandLogo(brandWhite)
    }
  }, [isAuth])

  useEffect(() => {
    const token = sessionStorage.getItem("token") || localStorage.getItem("token")
    setIsAuth(!!token)
    setAuthReady(true)
  }, [])

  useEffect(() => {
    const onAuthChange = () => {
      const t = sessionStorage.getItem("token") || localStorage.getItem("token")
      setIsAuth(!!t)
      setRoutesReady(false) // vamos a reconstruir el árbol
    }
    window.addEventListener("auth:login", onAuthChange)
    window.addEventListener("storage", onAuthChange)
    return () => {
      window.removeEventListener("auth:login", onAuthChange)
      window.removeEventListener("storage", onAuthChange)
    }
  }, [])

  // Detección proactiva de expiración para sesiones embed.
  // Programa un redirect ~30s antes de que el JWT expire, evitando que el usuario
  // experimente un 401 durante una petición activa.
  useEffect(() => {
    if (!isAuth) return
    const isEmbedSession = sessionStorage.getItem(EMBED_SESSION_FLAG_KEY) === "1"
    const embedApiKey = sessionStorage.getItem(EMBED_API_KEY_STORAGE_KEY) || ""
    if (!isEmbedSession || !embedApiKey) return

    const token = sessionStorage.getItem("token")
    if (!token) return

    let exp
    try {
      exp = JSON.parse(atob(token.split(".")[1]))?.exp
    } catch {
      return
    }
    if (!exp) return

    const msUntilRenew = exp * 1000 - Date.now() - 30_000
    const doRenew = () => {
      clearAuthStorage()
      notifyParent("PORTAL_EMBED_AUTH_EXPIRED")
      window.location.href = withBasename(`/embed?apikey=${encodeURIComponent(embedApiKey)}`)
    }

    if (msUntilRenew <= 0) {
      doRenew()
      return
    }

    const timer = setTimeout(doRenew, msUntilRenew)
    return () => clearTimeout(timer)
  }, [isAuth])

  useEffect(() => {
    if (!authReady) return
    if (!isAuth) {
      setRoutes(MyRoutes())
      setRoutesReady(true)
      return
    }
    let abort = false
    ;(async () => {
      try {
        const token = sessionStorage.getItem("token") || localStorage.getItem("token")
        if (!token) {
          setIsAuth(false)
          const pub = MyRoutes()
          const pubJSON = JSON.stringify(pub)
          if (pubJSON !== lastRoutesJSON.current) {
            lastRoutesJSON.current = pubJSON
            setRoutes(pub)
          }
          setRoutesReady(true)
          return
        }
        let url = `${config.api.baseURL}/auth/menus`
        const res = await fetch(url, {
          method: "GET",
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
        })
        if (res.status === 401 || res.status === 403) {
          const isEmbedSession = sessionStorage.getItem(EMBED_SESSION_FLAG_KEY) === "1"
          if (isEmbedSession) {
            // Para sesiones embed, el token puede ser válido aunque /auth/menus devuelva 401
            // (el usuario embed puede no tener menús configurados). No limpiar la sesión;
            // usar rutas de fallback para evitar el loop /embed → /dashboard → /embed.
            const storedToken = sessionStorage.getItem("token")
            let isTokenExpired = true
            if (storedToken) {
              try {
                const p = JSON.parse(atob(storedToken.split(".")[1]))
                isTokenExpired = !p?.exp || p.exp * 1000 - Date.now() < 30_000
              } catch { /* token malformado */ }
            }
            if (!isTokenExpired) {
              // Token vigente: usar rutas de fallback y mantener la sesión
              if (!abort) {
                const fallback = buildRoutesFromServer([]) // lista vacía → MyRoutes fallback
                const next = fallback.length ? fallback : MyRoutes(role)
                const nextJSON = JSON.stringify(next)
                if (nextJSON !== lastRoutesJSON.current) {
                  lastRoutesJSON.current = nextJSON
                  setRoutes(next)
                }
                setRoutesReady(true)
              }
              return
            }
          }
          // Sesión inválida o expirada: limpiar y pasar a rutas públicas
          sessionStorage.removeItem("token")
          localStorage.removeItem("token")
          setIsAuth(false)
          const pub = MyRoutes() // rutas públicas
          const pubJSON = JSON.stringify(pub)
          if (pubJSON !== lastRoutesJSON.current) {
            lastRoutesJSON.current = pubJSON
            setRoutes(pub)
          }
          setRoutesReady(true)
          return // Importante: cortar el flujo para no seguir
        }
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        const serverRoutes = await res.json()

        if (!abort) {
          const built = buildRoutesFromServer(serverRoutes)
          const next = built.length ? built : MyRoutes(role)
          // Evitar setRoutes si no hay cambios REALES (previene renders en cascada)
          const nextJSON = JSON.stringify(next)
          if (nextJSON !== lastRoutesJSON.current) {
            lastRoutesJSON.current = nextJSON
            setRoutes(next)
          }
          setRoutesReady(true)
        }
      } catch (e) {
        if (!abort) {
          const fallback = MyRoutes(role)
          const fallbackJSON = JSON.stringify(fallback)
          if (fallbackJSON !== lastRoutesJSON.current) {
            lastRoutesJSON.current = fallbackJSON
            setRoutes(fallback)
          }
          setRoutesReady(true)
        }
      }
    })()
    return () => {
      abort = true
    }
  }, [role, authReady, isAuth])

  useEffect(() => {
    if (!isAuth) {
      setBrandLogo(brandWhite)
      return
    }

    const cached = getCachedBrandLogo()
    if (cached !== undefined) {
      setBrandLogo(cached || brandWhite)
      return
    }

    fetchAndCacheBrandLogo()
  }, [isAuth, fetchAndCacheBrandLogo])

  useEffect(() => {
    const handleBrandLogoRefresh = () => {
      fetchAndCacheBrandLogo()
    }

    window.addEventListener("brand-logo:refresh", handleBrandLogoRefresh)
    return () => {
      window.removeEventListener("brand-logo:refresh", handleBrandLogoRefresh)
    }
  }, [fetchAndCacheBrandLogo])

  // Rutas ocultas del menú lateral (la ruta sigue accesible por URL para quien
  // tenga el permiso). Temporal: hasta poder asignar INGRESOS_RAW_VER en prod.
  const HIDDEN_MENU_ROUTES = ["/ingresos-raw"]
  const sidenavRoutes = useMemo(() => {
    const filtrar = (items) =>
      (items || [])
        .filter((it) => !HIDDEN_MENU_ROUTES.includes(it.route))
        .map((it) => (it.collapse ? { ...it, collapse: filtrar(it.collapse) } : it))
    return filtrar(routes)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routes])

  const handleMouseEnter = useCallback(() => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false)
      setOnMouseEnter(true)
    }
  }, [miniSidenav, onMouseEnter, dispatch])

  const handleMouseLeave = useCallback(() => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true)
      setOnMouseEnter(false)
    }
  }, [onMouseEnter, dispatch])
  // Setting the dir attribute for the body element
  useEffect(() => {
    document.body.setAttribute("dir", "ltr")
  }, [])

  // Setting page scroll to 0 when changing the route
  useEffect(() => {
    document.documentElement.scrollTop = 0
    document.scrollingElement.scrollTop = 0
  }, [pathname])

  if (!authReady || !routesReady) {
    return (
      <ThemeProvider theme={darkMode ? themeDark : theme}>
        <CssBaseline />
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100vh",
          }}
        >
          <span>Cargando…</span>
        </div>
      </ThemeProvider>
    )
  }

  const getRoutes = (allRoutes) =>
    allRoutes.map((route) => {
      if (route.collapse) {
        return getRoutes(route.collapse)
      }
      if (route.route) {
        return <Route exact path={route.route} element={route.component} key={route.key} />
      }
      return null
    })

  return (
    <ThemeProvider theme={darkMode ? themeDark : theme}>
      <CssBaseline />
      {layout === "dashboard" && isAuth && (
        <>
          <Sidenav
            color={sidenavColor}
            brand={brandLogo}
            brandName="Portal de Monitoreo"
            version_={packageInfo.version}
            routes={sidenavRoutes} // memo
            onMouseEnter={handleMouseEnter} // memo
            onMouseLeave={handleMouseLeave} // memo
          />
          <Configurator />
        </>
      )}
      {layout === "vr" && <Configurator />}
      <Routes>
        {getRoutes(routes)}
        <Route
          path="*"
          element={<Navigate to={isAuth ? "/dashboard" : "/authentication/sign-in"} />}
        />
      </Routes>
    </ThemeProvider>
  )
}
